'use client';

import { RootState } from '@/src/redux/store';
import AuctionTimer from './AuctionTimer';
import AuctionCurrentBid from './AuctionCurrentBid';
import { useSelector } from 'react-redux';
import { useNow, useTranslations } from 'next-intl';
import BidHistoryList from './BidHistoryList';
import AuctionBidController from './AuctionBidController';
import AuctionAutoBidController from './AuctionAutoBidController';
import AuctionDetails from './AuctionDetails';
import AuctionEndingDate from './AuctionEndingDate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from '@components/Accordion';
import {
  customerState,
  customerTypes,
} from '@/src/redux/customerSlice/customerSlice';
import LoadingBlock from '@components/loadingSkeletons/loadingBlock';
import { FaCrown, FaWindowClose } from 'react-icons/fa';
import { PiConfetti } from 'react-icons/pi';
import { IoBanOutline } from 'react-icons/io5';
import { IoMdInformationCircle } from 'react-icons/io';
import useFormatterPrice from '@/src/utils/formatter-price';
import { useLazyGetAuctionCustomerAutoBidsQuery } from '@api/api';
import isCustomersBid from '@/src/utils/isCustomersBid';
import useShowPrice from '@/src/utils/useShowPrice';

export default function AuctionSidebar({ ad }: { ad: any }) {
  const t = useTranslations('page.item.auction');
  const a = useTranslations('AuctionAutoBidController');
  const c = useTranslations('AuctionCurrentBid');
  const formatterPrice = useFormatterPrice();
  const showPrice = useShowPrice(ad);
  const now = useNow({
    updateInterval: 1000,
  });
  const customer: customerState = useSelector((state: RootState) => {
    return state.customer;
  });
  const auctionState: any | boolean = useSelector((state: RootState) => {
    if (!ad || !ad.hasOwnProperty('ad_auction') || !ad.ad_auction) return false;
    return state.auctions[ad.ad_auction.id] ?? false;
  });

  const vatExempt =
    auctionState.current_bid?.itemized_costs?.object?.vat_description ===
      'vat.exemption' ||
    ad.ad_auction.itemized_costs?.object?.vat_description === 'vat.exemption' ||
    ad.customer?.is_private;

  const hasCustomerPlacedBid = useCallback(
    (bids: any[]) => {
      if (!bids) {
        return false;
      }
      return bids.some((bid) => isCustomersBid(customer, bid));
    },
    [customer]
  );

  const getCustomerAutoBid = useCallback(
    (bids: any[] | any): number => {
      let autoBid = 0;
      if (!bids || !bids.length) {
        return 0;
      }
      [...bids].forEach((bid) => {
        if (autoBid) {
          return;
        }
        if (isCustomersBid(customer, bid)) {
          autoBid = parseInt(bid.amount_max);
        }
      });
      return autoBid;
    },
    [customer]
  );

  const countCustomerAutoBids = useCallback(
    (bids: any[] | any) => {
      let count = 0;
      if (!bids.length) {
        return 0;
      }
      bids.forEach((bid: any) => {
        if (
          isCustomersBid(customer, bid) &&
          bid.source_type === 'App\\Models\\AdAuctionAutoBid'
        ) {
          count++;
        }
      });
      return count;
    },
    [customer]
  );

  const [auction, setAuction] = useState(
    auctionState ? { ...ad.ad_auction, ...auctionState } : ad.ad_auction
  );
  const [getCustomerAutoBids, { data: customerBidsData }] =
    useLazyGetAuctionCustomerAutoBidsQuery();
  const customerBids = useMemo(() => {
    return customerBidsData?.data?.customer_auto_bids?.length
      ? [...customerBidsData?.data?.customer_auto_bids].reverse()
      : [];
  }, [customerBidsData]);
  const getCustomerAutoBidsWrapper = useCallback(() => {
    getCustomerAutoBids({
      auctionId: auction.id,
      customerId: customer.id || 0,
      customerType: customer.type || customerTypes.company,
    });
  }, [auction, customer, getCustomerAutoBids]);
  const auctionHasBid = auction.bids?.length;
  const auctionAmount = auction.current_bid?.amount
    ? auction.current_bid?.amount
    : auction.amount_start;
  const reserve = auction.amount_min ? auction.amount_min : false;
  const auctionEnded = auction
    ? new Date(auction.time_end_extended).getTime() < now.getTime()
    : false;
  const [customerIsTakingPart, setCustomerIsTakingPart] = useState(
    auction ? hasCustomerPlacedBid(auction?.bids) : false
  );
  const [customerIsWinning, setCustomerWinning] = useState(
    auction ? isCustomersBid(customer, auction?.current_bid) : false
  );
  const [customerAutoBid, setCustomerAutoBidAmount] = useState(
    getCustomerAutoBid(customerBids)
  );
  const [initialCustomersAutoBidsCount, setInitialCustomersAutoBidsCount] =
    useState(countCustomerAutoBids(auction?.bids || []));

  const [closeSignal, setCloseSignal] = useState(false);

  const signal = (autoBidAmount?: number) => {
    setCloseSignal(!closeSignal);
    if (autoBidAmount) {
      setCustomerAutoBidAmount(autoBidAmount);
      setInitialCustomersAutoBidsCount(initialCustomersAutoBidsCount + 1);
    }
  };

  useEffect(() => {
    setCustomerIsTakingPart(
      auction ? hasCustomerPlacedBid(auction?.bids) : false
    );
    setCustomerWinning(isCustomersBid(customer, auction?.current_bid));
    setInitialCustomersAutoBidsCount(
      countCustomerAutoBids(auction?.bids || [])
    );
  }, [customer, auction, countCustomerAutoBids, hasCustomerPlacedBid]);

  useEffect(() => {
    setAuction(
      auctionState ? { ...ad.ad_auction, ...auctionState } : ad.ad_auction
    );
  }, [ad, auctionState]);

  useEffect(() => {
    setCustomerAutoBidAmount(getCustomerAutoBid(customerBids));
  }, [customerBids, getCustomerAutoBid]);

  useEffect(() => {
    if (!customer.id) {
      return;
    }
    getCustomerAutoBidsWrapper();
  }, [customer, getCustomerAutoBidsWrapper]);

  useEffect(() => {
    const newCustomersAutoBidsCount = countCustomerAutoBids(
      auction?.bids || []
    );
    if (newCustomersAutoBidsCount !== initialCustomersAutoBidsCount) {
      getCustomerAutoBidsWrapper();
    }
  }, [auction, countCustomerAutoBids, getCustomerAutoBidsWrapper]);

  if (!auction) {
    return (
      <div className='mx-auto w-full'>
        <LoadingBlock />
      </div>
    );
  }

  const auctionInactiveNode = (
    <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-dark p-3 text-center text-2xl font-bold text-white'>
      <span className='shrink-0'>
        <IoBanOutline />
      </span>
      {t('auctionNotActive')}
    </div>
  );

  const showInactiveMessage =
    !auction || [1, 2, 5, 6, 7].includes(auction?.status);

  return (
    <>
      <AuctionEndingDate id={ad.id} end={auction.time_end_extended} />
      {!auctionEnded && <AuctionTimer end={auction.time_end_extended} />}
      {showInactiveMessage ? (
        auctionInactiveNode
      ) : (
        <>
          {auctionEnded &&
            customerIsWinning &&
            (!reserve || reserve <= auctionAmount) && (
              <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-positive p-3 text-center text-2xl font-bold text-white'>
                <span className='shrink-0'>
                  <FaCrown />
                </span>{' '}
                {t('youWon')}
              </div>
            )}
          {auctionEnded && customerIsTakingPart && !customerIsWinning && (
            <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-brand-alt p-3 text-center text-2xl font-bold text-white'>
              <span className='shrink-0'>
                <FaWindowClose />
              </span>{' '}
              {t('youLost')}
            </div>
          )}
          {auctionEnded &&
            (!reserve || reserve <= auctionAmount) &&
            !customerIsTakingPart && (
              <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-dark p-3 text-center text-2xl font-bold text-white'>
                <span className='shrink-0'>
                  <PiConfetti />
                </span>
                {t('ended')}
              </div>
            )}
          {auctionEnded && reserve && auctionAmount < reserve && (
            <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-dark p-3 text-center text-2xl font-bold text-white'>
              <span className='shrink-0'>
                <IoMdInformationCircle />
              </span>
              {c('reserveNotMet')}
            </div>
          )}
        </>
      )}
      <AuctionCurrentBid
        bid={auctionAmount}
        itemizedCosts={
          auctionState.current_bid?.itemized_costs ||
          ad.ad_auction?.itemized_costs
        }
        hasBid={auctionHasBid}
        reserve={reserve}
        showPrice={showPrice}
        isPrivate={!!ad.customer?.is_private}
      />
      {showPrice && (
        <div className='mb-4 block w-full px-6 text-center text-xs font-semibold uppercase'>
          {t('priceIncludedAuctionFee')}
        </div>
      )}
      {!auctionEnded && (
        <>
          {customerIsTakingPart && !customerIsWinning && (
            <div className='mx-6 mb-4 flex items-center justify-center gap-2 rounded bg-brand-alt p-3 text-center text-2xl font-bold text-white'>
              <span className='shrink-0'>
                <FaWindowClose />
              </span>{' '}
              {t('youAreOutbid')}
            </div>
          )}
          {customerIsTakingPart && customerIsWinning ? (
            <div className='mx-6 mb-4 flex items-center justify-center  gap-2 rounded bg-positive p-3 text-center text-2xl font-bold text-white'>
              <span className='shrink-0'>
                <FaCrown />
              </span>{' '}
              {t('youAreWinning')}
            </div>
          ) : (
            <>
              <span className='block w-full px-6 text-center text-xs font-semibold uppercase'>
                {vatExempt ? (
                  <>
                    {c('vatExemptItem')}.{' '}
                    <span className='font-normal'>{t('costsIncluded')}</span>
                  </>
                ) : (
                  t('costsIncluded')
                )}
              </span>
              <div className='mt-1 px-6'>
                <AuctionBidController
                  auctionId={ad.ad_auction.id}
                  minimalBid={
                    auctionHasBid
                      ? auctionAmount + auction.amount_increment
                      : Math.max(auctionAmount, auction.amount_increment)
                  }
                  increment={auction.amount_increment}
                  auctionEnd={new Date(auction.time_end_extended).getTime()}
                  ad={ad}
                />
              </div>
            </>
          )}
          <div className='my-4 px-6'>
            <Accordion
              title={
                <>
                  <span className='uppercase'>{a('placeAutoBid')}</span>
                  {!!customerAutoBid && (
                    <span
                      className={`block text-sm ${customerAutoBid <= auctionAmount ? 'text-brand-alt' : ''}`}
                    >
                      {a('yourAutoBid', {
                        bidValue: formatterPrice(
                          customerAutoBid,
                          process.env.NEXT_PUBLIC_DEFAULT_LOCALE
                        ),
                      })}
                    </span>
                  )}
                </>
              }
              theme='yellow'
              innerPadding={false}
              signal={closeSignal}
            >
              <div className='py-3'>
                <AuctionAutoBidController
                  auctionId={auction.id}
                  minimalBid={
                    auctionHasBid
                      ? auctionAmount + auction.amount_increment
                      : Math.max(auctionAmount, auction.amount_increment)
                  }
                  increment={auction.amount_increment}
                  customerAutoBid={customerAutoBid}
                  autofill={false}
                  auctionEnd={new Date(auction.time_end_extended).getTime()}
                  ad={ad}
                  onSuccess={signal}
                />
              </div>
            </Accordion>
          </div>
        </>
      )}
      <AuctionDetails ad={ad} />
      <BidHistoryList auction={auction} />
    </>
  );
}
